import React, { useState, useEffect } from 'react';
import { Lock, Phone, User, Building2, DollarSign, Briefcase, Clock, ChevronDown, ChevronUp } from 'lucide-react';

const API_URL = 'https://lisa-realestate-worker.xelsed.workers.dev';

const Admin = () => {
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [authError, setAuthError] = useState('');
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === 'apple1*') {
      setAuthenticated(true);
      setAuthError('');
      sessionStorage.setItem('admin_auth', 'true');
    } else {
      setAuthError('Incorrect password');
    }
  };

  useEffect(() => {
    if (sessionStorage.getItem('admin_auth') === 'true') {
      setAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (!authenticated) return;
    setLoading(true);
    fetch(`${API_URL}/api/leads`, {
      headers: { 'x-admin-key': 'apple1*' },
    })
      .then((r) => r.json())
      .then((data) => {
        setLeads(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [authenticated]);

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  };

  const scoreBadge = (score) => {
    const colors = {
      good: 'bg-green-100 text-green-800',
      fair: 'bg-yellow-100 text-yellow-800',
      'needs-review': 'bg-red-100 text-red-800',
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[score] || 'bg-gray-100 text-gray-800'}`}>
        {score || 'pending'}
      </span>
    );
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-sm">
          <div className="text-center mb-6">
            <div className="bg-primary-100 rounded-full p-3 w-14 h-14 mx-auto mb-3 flex items-center justify-center">
              <Lock className="h-7 w-7 text-primary-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Admin Access</h1>
            <p className="text-gray-600 text-sm mt-1">Enter the admin password to view leads</p>
          </div>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-3 focus:outline-none focus:ring-2 focus:ring-primary-500"
              autoFocus
            />
            {authError && <p className="text-red-600 text-sm mb-3">{authError}</p>}
            <button type="submit" className="w-full bg-primary-600 text-white py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors">
              Sign In
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Call Leads</h1>
            <p className="text-gray-600 text-sm mt-1">Notes and info from inbound phone calls</p>
          </div>
          <button
            onClick={() => { sessionStorage.removeItem('admin_auth'); setAuthenticated(false); }}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Sign Out
          </button>
        </div>

        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto mb-3"></div>
            <p className="text-gray-600">Loading leads...</p>
          </div>
        ) : leads.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <Phone className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 mb-1">No leads yet</h3>
            <p className="text-gray-500 text-sm">Leads will appear here after callers speak with the AI phone agent.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {leads.map((lead) => (
              <div key={lead.id} className="bg-white rounded-lg shadow overflow-hidden">
                <div
                  className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setExpandedId(expandedId === lead.id ? null : lead.id)}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <div className="bg-primary-100 rounded-full p-2">
                        <User className="h-4 w-4 text-primary-600" />
                      </div>
                      <div>
                        <span className="font-semibold text-gray-900">{lead.caller_name || 'Unknown'}</span>
                        {lead.interested_property && (
                          <span className="text-gray-500 text-sm ml-2">— {lead.interested_property}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {scoreBadge(lead.qualification_score)}
                      <span className="text-xs text-gray-400">{formatDate(lead.created_at)}</span>
                      {expandedId === lead.id
                        ? <ChevronUp className="h-4 w-4 text-gray-400" />
                        : <ChevronDown className="h-4 w-4 text-gray-400" />
                      }
                    </div>
                  </div>
                </div>

                {expandedId === lead.id && (
                  <div className="border-t border-gray-100 p-4 bg-gray-50">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-1">
                          <User className="h-3.5 w-3.5" /> Contact
                        </h4>
                        <p><span className="text-gray-500">Name:</span> {lead.caller_name || '—'}</p>
                        <p><span className="text-gray-500">Phone:</span> {lead.caller_phone || '—'}</p>
                        <p><span className="text-gray-500">Email:</span> {lead.caller_email || '—'}</p>
                        <p><span className="text-gray-500">Source:</span> {lead.source || '—'}</p>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-1">
                          <Building2 className="h-3.5 w-3.5" /> Property
                        </h4>
                        <p><span className="text-gray-500">Interested in:</span> {lead.interested_property || '—'}</p>
                        <p><span className="text-gray-500">Budget:</span> {lead.budget || '—'}</p>
                        <p><span className="text-gray-500">Intended use:</span> {lead.intended_use || '—'}</p>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-1">
                          <Briefcase className="h-3.5 w-3.5" /> Background
                        </h4>
                        <p><span className="text-gray-500">Business:</span> {lead.business_type || '—'}</p>
                        <p><span className="text-gray-500">Years:</span> {lead.years_in_business || '—'}</p>
                        <p><span className="text-gray-500">Occupation:</span> {lead.current_occupation || '—'}</p>
                        <p><span className="text-gray-500">Employees:</span> {lead.num_employees || '—'}</p>
                        <p><span className="text-gray-500">Experience:</span> {lead.tenant_experience || '—'}</p>
                      </div>
                    </div>
                    {(lead.business_background || lead.notes) && (
                      <div className="mt-3 space-y-2">
                        {lead.business_background && (
                          <div className="bg-white rounded border border-gray-200 p-3">
                            <span className="text-xs font-semibold text-gray-500 uppercase">Background</span>
                            <p className="text-sm text-gray-800 mt-1">{lead.business_background}</p>
                          </div>
                        )}
                        {lead.notes && (
                          <div className="bg-white rounded border border-gray-200 p-3">
                            <span className="text-xs font-semibold text-gray-500 uppercase">Notes</span>
                            <p className="text-sm text-gray-800 mt-1">{lead.notes}</p>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="mt-2 text-xs text-gray-400">
                      ID: {lead.id}{lead.call_id ? ` | Call: ${lead.call_id}` : ''}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
